import { type JSX, useEffect, useRef, useState } from 'react';

import {
  EINHEITSNAME,
  TARIFE,
  TARIFNAME,
  alsBetragstext,
  tabelleFortschreiben,
  type Honorartarif,
  type Leistungseinheit,
  type Wirksamzeile,
} from '../api/honorare';
import { alsEurosatz } from '../betrag';

/**
 * Das Honorargitter — Kategorien mal Tarife zu einem STICHTAG (RES-68, RES-72).
 *
 * ============================================================================
 * DER STICHTAG IST DIE ERSTE ZEILE DIESER MASKE (0190)
 * ============================================================================
 *
 * Auftraggeber am 12.09.2026: „Die honorarsätze sollen konfigurierbar sein und
 * wie die Preislisten für die Produkte mit einem Datum versehen werden, um später
 * anpassbar und nachvollziehbar zu sein."
 *
 * Die Sätze TRUGEN schon ein Datum — `gueltigAb`/`gueltigBis` seit 0183, und der
 * Ausschluss in der Datenbank dagegen, dass zwei sich überschneiden. Gefehlt hat
 * das Datum an der MASKE: Dieses Gitter zeigte immer nur **heute**. Eine Fassung,
 * die ab Januar gilt, war damit bis zum 1. Januar unsichtbar — man konnte sie
 * schreiben und nicht ansehen. Das ist keine Nachvollziehbarkeit, sondern ein
 * Schreibvorgang ins Dunkle.
 *
 * Mit dem Stichtag stellt das Gitter eine Frage, die ein Datum hat: „Was zahlen
 * wir **an diesem Tag**?" Vorwärts ist das die Vorschau auf eine vorbereitete
 * Fassung, rückwärts der Beweis, was damals galt.
 *
 * UND ER IST DIE GRUNDLAGE DER BEARBEITUNG. „Tabelle bearbeiten" belegt
 * `gültig ab` mit dem Stichtag vor — wer die Sätze für den 1. Januar ansieht und
 * dann tippt, meint den 1. Januar. Stünde dort weiter „heute", schriebe er die
 * Januarfassung mit den Zahlen vom September zu.
 *
 * ============================================================================
 * WARUM EIN GITTER UND NICHT EINE KARTE JE KATEGORIE
 * ============================================================================
 *
 * Seit dem 12.09.2026 zahlt das Haus nach Kategorie: je Fahrtraining eine, dazu
 * zwei Stundenzeilen (Stundensatz, Stundensatz Koordinator), die an keinem
 * Training hängen. Jede Kategorie hat einen Wochentags- und einen
 * Wochenendtarif — bei sechsunddreißig Trainings sind das sechsundsiebzig
 * Beträge.
 *
 * Als Karte je Kategorie wären das achtunddreißig Abschnitte mit je zwei
 * Formularen und achtunddreißig Daten. Die Frage, die man an eine Honorartabelle
 * stellt, ist aber eine Vergleichsfrage: „Was zahlen wir am Wochenende mehr?"
 * Die beantwortet nur ein Gitter, weil dort die Spalten nebeneinander stehen.
 *
 * ============================================================================
 * DIE EINHEIT STEHT AN DER ZEILE UND NICHT AN DER SPALTE
 * ============================================================================
 *
 * „Pkw · je Tag" und „Stundensatz · je Stunde" — die Kategorie entscheidet,
 * worin gezählt wird (0187). Bis zum Mittag des 12.09.2026 stand die Einheit an
 * der SPALTE, weil der Stundensatz eine war; 400,00 und 62,50 standen damit
 * nebeneinander, als wären sie vergleichbar.
 *
 * ============================================================================
 * ES SIEHT WIE ÄNDERN AUS UND IST FORTSCHREIBEN (RES-72)
 * ============================================================================
 *
 * In den Zellen stehen Eingabefelder, und trotzdem wird kein Betrag
 * überschrieben. Was der Server daraus macht, sind zwei Schritte je geänderter
 * Zelle: den geltenden Satz zum Vortag schließen, den neuen ab dem gewählten Tag
 * anlegen. „Ein bereits erteilter Auftrag behält seinen Satz auch dann, wenn die
 * Tabelle später geändert wird."
 *
 * DIE MASKE SAGT DAS AUCH. Ein Eingabefeld, das nach Überschreiben aussieht und
 * fortschreibt, ist sonst eine Überraschung — und zwar in vier Wochen, wenn
 * jemand die frühere Fassung sucht und sie noch da ist.
 *
 * ============================================================================
 * EINE GELEERTE ZELLE IST EINE ANGABE UND KEIN VERSEHEN
 * ============================================================================
 *
 * Wer einen Betrag löscht, sagt: „Diesen Tarif zahlen wir nicht mehr." Der
 * bestehende Satz wird geschlossen, ein Nachfolger entsteht nicht. In der
 * Trainerakte heißt das danach: Es gilt wieder die allgemeine Vorgabe.
 *
 * Das steht als Satz unter dem Gitter, weil es die eine Handlung ist, die man
 * nicht rückgängig macht, ohne die Zahl noch zu kennen.
 */

/** Der Schlüssel einer Zelle — Zeile und Spalte in einem Wort. */
function zelle(leistungsart: string, tarif: Honorartarif): string {
  return `${leistungsart}/${tarif}`;
}

/** Heute als JJJJ-MM-TT. */
function heute(): string {
  const jetzt = new Date();
  return [
    String(jetzt.getFullYear()),
    String(jetzt.getMonth() + 1).padStart(2, '0'),
    String(jetzt.getDate()).padStart(2, '0'),
  ].join('-');
}

/*
 * DER STICHTAG IM VERHÄLTNIS ZU HEUTE.
 *
 * Ein Datumsfeld allein sagt nicht, was es bedeutet. Drei Fälle, drei Sätze:
 * heute ist der Regelfall und braucht keinen, vorwärts ist eine Vorschau,
 * rückwärts ein Nachweis. Ohne diese Marke liest jemand eine vorbereitete
 * Fassung als die geltende — und das ist der einzige Fehler, den diese Maske
 * wirklich anrichten kann.
 */
type Zeitlage = 'heute' | 'kuenftig' | 'frueher';

function zeitlageVon(stichtag: string, heuteIst: string): Zeitlage {
  return stichtag === heuteIst ? 'heute' : stichtag > heuteIst ? 'kuenftig' : 'frueher';
}

function Zeitlagesatz({ zeitlage }: { readonly zeitlage: Zeitlage }): JSX.Element {
  return (
    <>
      {zeitlage === 'heute' && <>Was heute gilt.</>}
      {zeitlage === 'kuenftig' && (
        <>
          <span className="marke marke--warn">noch nicht in Kraft</span> Eine vorbereitete
          Fassung — so wird die Tabelle an diesem Tag aussehen.
        </>
      )}
      {zeitlage === 'frueher' && (
        <>
          <span className="marke">vergangen</span> Was an diesem Tag galt. Frühere Fassungen
          bleiben stehen.
        </>
      )}
    </>
  );
}

/**
 * DER STICHTAG IN DER WERKZEUGLEISTE (ÄA-41, Seitentyp Planung; 0190, RES-68).
 *
 * Die Honorartabelle ist eine Planung: oben die Werkzeuge, darunter das
 * Gitter in voller Breite. Der Stichtag steht deshalb dort und nicht über dem
 * Gitter — dasselbe Bedienfeld, dieselben drei Sätze wie im Gitter der
 * Trainerakte, nur in einer Zeile.
 *
 * `gesperrt`, solange das Gitter bearbeitet wird: Die Felder zeigen die
 * Beträge dieses Tages, und ein Sprung mitten im Tippen legte Zahlen eines
 * anderen Tages unter die Eingaben.
 */
export function Stichtagswahl({
  stichtag,
  beiStichtag,
  gesperrt,
}: {
  readonly stichtag: string;
  readonly beiStichtag: (tag: string) => void;
  readonly gesperrt: boolean;
}): JSX.Element {
  const heuteIst = heute();
  const zeitlage = zeitlageVon(stichtag, heuteIst);
  return (
    <>
      <label className="liste__filter">
        Sätze zum{' '}
        <input
          type="date"
          aria-label="Sätze zum"
          value={stichtag}
          disabled={gesperrt}
          onChange={(e) => {
            if (e.target.value !== '') beiStichtag(e.target.value);
          }}
        />
      </label>
      <span className="hilfe">
        <Zeitlagesatz zeitlage={zeitlage} />
      </span>
      {zeitlage !== 'heute' && !gesperrt && (
        <button
          type="button"
          className="knopf knopf--verweis"
          onClick={() => {
            beiStichtag(heuteIst);
          }}
        >
          zurück zu heute
        </button>
      )}
    </>
  );
}

export function Honorargitter({
  wirksam,
  stichtag,
  beiStichtag,
  tabelle,
  stufe,
  darfAendern,
  laedt,
  beiHandgriff,
  beiStilllegen,
  stichtagInWerkzeugleiste = false,
  beiBearbeiten,
}: {
  /** Eine Zeile je Zelle — auch die leeren, sonst fehlt die Lücke im Gitter. */
  readonly wirksam: readonly Wirksamzeile[];
  /**
   * Der Tag, für den `wirksam` gilt (0190).
   *
   * Das Gitter RECHNET ihn nicht aus — in der allgemeinen Tabelle bildet die
   * Maske `wirksam` selbst aus den Sätzen, in der Trainerakte kommt es vom
   * Server, weil sich dort zwei Stufen kreuzen. Beide brauchen dasselbe
   * Bedienfeld, und deshalb steht es hier und der Wert dort.
   */
  readonly stichtag: string;
  /** Ein anderer Stichtag. Was daraufhin nachgeladen wird, weiß der Aufrufer. */
  readonly beiStichtag: (tag: string) => void;
  /**
   * Das GEFÄSS, in das dieses Gitter schreibt (0191).
   *
   * Vorher stand hier `ressource`. Die Stufe hing am Satz; seit 0191 hängt sie an
   * der Tabelle, und das Gitter braucht deren Kennung, um überhaupt schreiben zu
   * können.
   */
  readonly tabelle: string;
  /**
   * RES-69: Welche Stufe dieses Gefäß ist — die allgemeine Vorgabe des Hauses
   * oder die eigene Vereinbarung eines Trainers.
   *
   * Sie entscheidet zwei Dinge: die Beschriftung, und was in einer Zelle als
   * GEERBT gilt (siehe `eigenerBetrag`).
   */
  readonly stufe: 'allgemein' | 'ressource';
  readonly darfAendern: boolean;
  readonly laedt: boolean;
  /**
   * Der Handgriff des Aufrufers: ausführen, Meldung setzen, neu laden. Das
   * Gitter lädt NICHT selbst — es kennt seine Quelle nicht. In der allgemeinen
   * Tabelle kommt `wirksam` aus den Sätzen, in der Trainerakte vom Server.
   */
  readonly beiHandgriff: (tun: () => Promise<unknown>, sagen: string) => void;
  /**
   * Eine Kategorie stilllegen — NUR in der allgemeinen Tabelle (12.09.2026).
   *
   * Sie ist eine Handlung am Bestand des Hauses und nicht an diesem Trainer;
   * in der Trainerakte bleibt sie deshalb weg, und ohne diesen Handgriff
   * zeichnet das Gitter die Spalte gar nicht.
   *
   * Vorher stand sie in einer ZWEITEN Tabelle unter dem Gitter, die alle
   * Kategorien noch einmal auflistete. Auftraggeber: „Wieso gibt es zwei
   * honorartabellen?" — zu Recht: Seit die Kategorien aus den Produkten kommen,
   * war die zweite Tabelle ein Duplikat, dessen „Umbenennen" das Ladewerkzeug
   * beim nächsten Lauf überschrieben hätte.
   */
  readonly beiStilllegen?: (schluessel: string, bezeichnung: string) => void;
  /**
   * DER STICHTAG STEHT IN DER WERKZEUGLEISTE (ÄA-41) — in der Honorartabelle,
   * die als Planung gebaut ist (`Stichtagswahl`). Das Gitter zeichnet dann
   * kein eigenes Feld; in der Trainerakte bleibt es, wo es war.
   */
  readonly stichtagInWerkzeugleiste?: boolean;
  /** Meldet, ob gerade bearbeitet wird — die Werkzeugleiste sperrt dann den Stichtag. */
  readonly beiBearbeiten?: (offen: boolean) => void;
}): JSX.Element {
  const [entwurf, setzeEntwurf] = useState<ReadonlyMap<string, string> | null>(null);
  const bearbeitet = entwurf !== null;
  /* Der Rückruf im Ref: Als Abhängigkeit setzte er die Meldung bei jedem Bild neu. */
  const melde = useRef(beiBearbeiten);
  melde.current = beiBearbeiten;
  useEffect(() => {
    melde.current?.(bearbeitet);
  }, [bearbeitet]);
  const [gueltigAb, setzeGueltigAb] = useState(stichtag);
  /** Der Name dieser Fassung (0190) — „Tarifrunde 2027". Freiwillig. */
  const [name, setzeName] = useState('');

  /*
   * Die Zeilen des Gitters, in der Reihenfolge, in der sie hereinkommen.
   *
   * Jede trägt ihre EINHEIT mit: Ein Fahrtraining zählt in Tagen, eine
   * Stundenzeile in Stunden (0187). Sie steht deshalb an der Zeile und nicht an
   * der Spalte.
   */
  const arten: {
    readonly schluessel: string;
    readonly bezeichnung: string;
    readonly einheit: Leistungseinheit;
  }[] = [];
  for (const w of wirksam) {
    if (!arten.some((a) => a.schluessel === w.leistungsart)) {
      arten.push({
        schluessel: w.leistungsart,
        bezeichnung: w.leistungsartName,
        einheit: w.einheit,
      });
    }
  }

  const finde = (leistungsart: string, tarif: Honorartarif): Wirksamzeile | undefined =>
    wirksam.find((w) => w.leistungsart === leistungsart && w.tarif === tarif);

  /**
   * Der Betrag, der WIRKLICH IN DIESER ZELLE STEHT — und nicht der, der gilt.
   *
   * Der Unterschied fällt in der Trainerakte an: Dort zeigt das Gitter in
   * Spalte „Wochenende" auch die allgemeine Vorgabe (weil sie für ihn gilt) und
   * einen ersatzweise eingesprungenen Wochentagssatz. Beides ist nicht SEIN
   * Satz für diese Zelle.
   *
   * Würde die Bearbeitung ihn trotzdem vorbelegen, entstünde beim ersten
   * Speichern ein eigener Satz mit genau dem Betrag der Vorgabe — und ab da
   * ändert eine Änderung der Vorgabe für diesen Trainer nichts mehr. Das ist
   * der stille Fehler, den diese Funktion verhindert.
   */
  const eigenerBetrag = (w: Wirksamzeile | undefined): string | null => {
    if (w === undefined || w.betrag === null || w.ersatzweise) return null;
    return w.stufe === stufe ? w.betrag : null;
  };

  const beginneBearbeitung = (): void => {
    const felder = new Map<string, string>();
    for (const art of arten) {
      for (const tarif of TARIFE) {
        const eigen = eigenerBetrag(finde(art.schluessel, tarif));
        felder.set(zelle(art.schluessel, tarif), eigen === null ? '' : alsBetragstext(eigen));
      }
    }
    setzeEntwurf(felder);
    /*
     * DAS DATUM KOMMT VOM STICHTAG UND NICHT VON HEUTE (0190).
     *
     * Wer die Sätze zum 1. Januar ansieht und „bearbeiten" drückt, bearbeitet die
     * Januarfassung. Stünde hier `heute()`, schriebe der erste Tastendruck die
     * Januarzahlen mit den Septemberzahlen zu — und zwar still, weil die Felder
     * genau so aussehen wie vorher.
     */
    setzeGueltigAb(stichtag);
    setzeName('');
  };

  const speichere = (): void => {
    if (entwurf === null) return;
    const zellen = arten.flatMap((art) =>
      TARIFE.map((tarif) => {
        const wert = (entwurf.get(zelle(art.schluessel, tarif)) ?? '').trim();
        return {
          leistungsart: art.schluessel,
          tarif,
          betrag: wert === '' ? null : wert,
        };
      }),
    );

    beiHandgriff(async () => {
      const ergebnis = await tabelleFortschreiben({
        tabelle,
        gueltigAb,
        zellen,
        bemerkung: null,
        /* Leer heißt ohne Namen — die Fassung heißt dann nach ihrem Datum (0190). */
        fassung: name.trim() === '' ? null : name.trim(),
      });
      setzeEntwurf(null);
      /*
       * DER STICHTAG WANDERT MIT. Nach dem Speichern will man sehen, was man
       * geschrieben hat; stünde er weiter auf dem alten Tag, zeigte das Gitter
       * danach die Fassung von vorher — und die Änderung sähe aus, als wäre sie
       * nicht angekommen.
       */
      beiStichtag(gueltigAb);
      return ergebnis;
    }, `Die Honorartabelle gilt ab ${gueltigAb}. Frühere Fassungen bleiben erhalten.`);
  };

  const heuteIst = heute();
  const zeitlage = zeitlageVon(stichtag, heuteIst);

  /* Der Stichtag bleibt bedienbar, auch wenn noch keine Kategorie steht. */
  const stichtagsfeld = stichtagInWerkzeugleiste ? null : (
    <div className="feldreihe">
      <div className="feld">
        <label htmlFor={`gitter-stichtag-${tabelle}`}>Sätze zum</label>
        <input
          id={`gitter-stichtag-${tabelle}`}
          className="feld__eingabe feld__eingabe--schmal"
          type="date"
          value={stichtag}
          disabled={entwurf !== null}
          onChange={(e) => {
            if (e.target.value !== '') beiStichtag(e.target.value);
          }}
        />
        <p className="feld__hilfe">
          <Zeitlagesatz zeitlage={zeitlage} />
        </p>
      </div>
      {zeitlage !== 'heute' && entwurf === null && (
        <div className="feld">
          <button
            type="button"
            className="knopf knopf--verweis"
            onClick={() => {
              beiStichtag(heuteIst);
            }}
          >
            zurück zu heute
          </button>
        </div>
      )}
    </div>
  );

  if (arten.length === 0) {
    return (
      <>
        {stichtagsfeld}
        <p className="liste__leer">
          Es ist keine Kategorie in Gebrauch. Legen Sie unten eine an — ohne sie gibt es
          keine Zeile, in die ein Honorar gehört.
        </p>
      </>
    );
  }

  return (
    <>
      {stichtagsfeld}
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle" aria-label="Honorarsätze je Kategorie und Tarif">
          <thead>
            <tr>
              <th scope="col">Kategorie</th>
              {TARIFE.map((tarif) => (
                <th scope="col" key={tarif}>
                  {TARIFNAME[tarif]}
                </th>
              ))}
              {beiStilllegen !== undefined && entwurf === null && (
                <th scope="col">Handlung</th>
              )}
            </tr>
          </thead>
          <tbody>
            {arten.map((art) => (
              <tr key={art.schluessel}>
                <th scope="row">
                  {art.bezeichnung}
                  <br />
                  {/*
                    Worin diese Zeile zählt: „je Training" bei einem Fahrtraining,
                    „je Stunde" bei den beiden Stundenzeilen (0187, 0189). Nicht
                    „je Tag" — vergütet wird je Training, und wie lange es läuft,
                    steht in seinem Programm.
                  */}
                  <span className="hilfe">{EINHEITSNAME[art.einheit]}</span>
                </th>
                {TARIFE.map((tarif) => {
                  const w = finde(art.schluessel, tarif);
                  const eigen = eigenerBetrag(w);
  
                  if (entwurf !== null) {
                    return (
                      <td key={tarif}>
                        <input
                          className="feld__eingabe feld__eingabe--schmal"
                          inputMode="decimal"
                          aria-label={`${art.bezeichnung}, ${TARIFNAME[tarif]} (netto)`}
                          /*
                           * Die geerbte Vorgabe steht als Platzhalter da und nicht
                           * als Wert: sichtbar, ohne beim Speichern mitzukommen.
                           */
                          placeholder={
                            eigen === null && w?.betrag != null
                              ? alsBetragstext(w.betrag)
                              : undefined
                          }
                          value={entwurf.get(zelle(art.schluessel, tarif)) ?? ''}
                          onChange={(e) => {
                            const naechster = new Map(entwurf);
                            naechster.set(zelle(art.schluessel, tarif), e.target.value);
                            setzeEntwurf(naechster);
                          }}
                        />
                      </td>
                    );
                  }
  
                  return (
                    <td className="zahl" key={tarif}>
                      {w?.betrag == null ? (
                        <span className="hilfe">—</span>
                      ) : (
                        <>
                          {alsEurosatz(w.betrag)}
                          {/*
                            Zwei Hinweise, die nur in der Trainerakte auftreten
                            können — und die man sehen muss, weil der Betrag sonst
                            wie ein eigener Satz aussieht.
                          */}
                          {w.ersatzweise && (
                            <>
                              {' '}
                              <span className="marke marke--warn">Wochentagstarif</span>
                            </>
                          )}
                          {!w.ersatzweise && eigen === null && (
                            <>
                              {' '}
                              <span className="marke">Vorgabe</span>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
                {beiStilllegen !== undefined && entwurf === null && (
                  <td>
                    {/*
                      AUSSER GEBRAUCH UND NICHT GELÖSCHT. Was die Kategorie trägt,
                      bleibt lesbar — eine Honorarposition nennt sie noch Jahre
                      später (RES-72). Angeboten wird sie danach nicht mehr, und
                      unter dem Gitter steht der Weg zurück.
                    */}
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      disabled={laedt}
                      onClick={() => {
                        beiStilllegen(art.schluessel, art.bezeichnung);
                      }}
                    >
                      Außer Gebrauch setzen
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {entwurf === null ? (
        darfAendern && (
          <div className="knopfreihe">
            <button type="button" className="knopf knopf--klein" onClick={beginneBearbeitung}>
              Tabelle bearbeiten
            </button>
          </div>
        )
      ) : (
        <>
          <p className="feld__hilfe">
            Beträge <strong>netto</strong>, in Euro. Eine geleerte Zelle schließt den Satz zum
            gewählten Tag — danach gilt{' '}
            {stufe === 'allgemein' ? 'für diesen Tarif nichts mehr' : 'wieder die allgemeine Vorgabe'}.
            Frühere Fassungen bleiben stehen; geändert wird kein Betrag.
          </p>
          <div className="feldreihe">
            <div className="feld">
              <label htmlFor="gitter-ab">gültig ab</label>
              <input
                id="gitter-ab"
                className="feld__eingabe feld__eingabe--schmal"
                type="date"
                value={gueltigAb}
                onChange={(e) => {
                  setzeGueltigAb(e.target.value);
                }}
              />
              <p className="feld__hilfe">
                {gueltigAb === stichtag ? (
                  <>Der Tag, den Sie angesehen haben.</>
                ) : (
                  /*
                    DIE EINE STELLE, AN DER MAN SICH VERGREIFEN KANN.

                    Die Felder zeigen die Beträge vom Stichtag; gespeichert werden
                    sie ab `gueltigAb`. Weichen die beiden voneinander ab, trägt
                    man die Zahlen eines Tages in einen anderen — manchmal genau
                    richtig („die Januarfassung soll dasselbe zahlen"), manchmal
                    ein Versehen. Die Maske entscheidet das nicht, sie sagt es.
                  */
                  <span className="marke marke--warn">
                    Die Beträge oben gelten zum {stichtag} und werden ab {gueltigAb}{' '}
                    hinterlegt.
                  </span>
                )}
              </p>
            </div>
            <div className="feld">
              <label htmlFor="gitter-fassung">Bezeichnung der Fassung</label>
              <input
                id="gitter-fassung"
                className="feld__eingabe"
                value={name}
                maxLength={80}
                placeholder="Tarifrunde 2027"
                onChange={(e) => {
                  setzeName(e.target.value);
                }}
              />
              {/*
                FREIWILLIG, und das steht auch da (0190). Wer eine einzelne Zelle
                berichtigt, hat keinen Namen dafür — ein Pflichtfeld erzwingt hier
                „Korrektur", und das sagt nichts. Ohne Namen heißt die Fassung
                nach ihrem Datum.
              */}
              <p className="feld__hilfe">
                Unter ihr steht die Fassung in der Übersicht — wie eine Preisliste unter
                ihrer Bezeichnung. Ohne Angabe heißt sie nach ihrem Datum.
              </p>
            </div>
          </div>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeEntwurf(null);
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laedt || gueltigAb === ''}
              onClick={speichere}
            >
              Tabelle speichern
            </button>
          </div>
        </>
      )}
    </>
  );
}
